import Box from '@/main/components/shared/box';
import { getTranslateByKey, isNullUndefinedEmpty } from '@/main/services/utils';

import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputAmountControlled from '@/main/components/shared/inputText/inputAmountControlled';
import { KOGTableRow } from '@/main/components/shared/kogBoxGrid/styles';
import {
  RowHeaderTextEnd,
  TextEllipsis
} from '@/main/components/shared/styles';
import { ID_BASE_COMPONENTS_REGISTER_MATRIX } from '@/main/screens/registration/approvalMatrix/registerApprovalMatrix/approvalMatrixInfo/approvalMatrixInfo';
import {
  approvalMatrixValueSchema,
  IApprovalMatrixRegisterFormOutput,
  IApprovalMatrixValueForm,
  IApprovalMatrixValueFormOutput,
  INITIAL_APPROVAL_MATRIX_VALUE_FORM
} from '@/main/screens/registration/approvalMatrix/registerApprovalMatrix/utilsRegisterApprovalMatrix';
import { isNullUndefined } from '@/main/services/reducerUtils';
import { currencyToNumber } from '@/main/services/utilsNumber';
import { useUserHierarchyReducer } from '@/main/store/userHierarchy';
import { zodResolver } from '@hookform/resolvers/zod';
import { Close, Edit } from '@mui/icons-material';
import { IconButton, TableCell } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  useFieldArray,
  useForm,
  useFormContext,
  useWatch
} from 'react-hook-form';
import { toast } from 'react-toastify';
import * as S from '../styles';

interface IApprovalMatrixValues {}

export default function ApprovalMatrixValues({}: IApprovalMatrixValues) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

  const {
    userHierarchy: {
      data: userHierarchyLevels,
      isLoading: isLoadingUserHierarchy
    }
  } = useUserHierarchyReducer();

  const {
    control: controlValueForm,
    setValue,
    reset,
    handleSubmit
  } = useForm<IApprovalMatrixValueForm, any, IApprovalMatrixValueFormOutput>({
    mode: 'onChange',
    resolver: zodResolver(approvalMatrixValueSchema),
    defaultValues: INITIAL_APPROVAL_MATRIX_VALUE_FORM
  });

  const { control } = useFormContext<IApprovalMatrixRegisterFormOutput>();
  const { append, update, fields, remove } = useFieldArray({
    control,
    name: 'approvalMatrixValues'
  });

  const selectedInsurer = useWatch({
    control,
    name: 'approvalMatrixInformation.insurer'
  });

  useEffect(() => {
    clearForm();
  }, [selectedInsurer]);

  function handleEdit(item: IApprovalMatrixValueFormOutput, index: number) {
    setEditingIndex(index);
    reset(item);
  }

  async function handleRemove(
    item: IApprovalMatrixValueFormOutput,
    index: number
  ) {
    const nextLevel = fields.at(index + 1);
    if (!isNullUndefined(nextLevel) && nextLevel.active) {
      const confirmed = await getConfirmation(
        getTranslateByKey('information.confirmRemoveMatrixLevel')
      );

      if (!confirmed) return;
    }
    if (isNullUndefinedEmpty(item.guid)) {
      remove(index);
      return;
    }
    const newItem: IApprovalMatrixValueFormOutput = {
      ...item,
      active: false
    };

    update(index, newItem);
  }

  function clearForm() {
    reset(INITIAL_APPROVAL_MATRIX_VALUE_FORM);
    setEditingIndex(null);
  }

  function validateLevelAddition(
    currentValues: IApprovalMatrixValueFormOutput[],
    newValue: IApprovalMatrixValueFormOutput
  ) {
    const lastValue = currentValues.at(-1);
    const expectedLevel = lastValue
      ? Number(lastValue.hierarchyLevel.key) + 1
      : 1;
    const newLevel = Number(newValue.hierarchyLevel.key);

    if (newLevel !== expectedLevel) {
      toast.error(
        getTranslateByKey('information.levelsMustBeConfiguredInSequence')
      );
      return false;
    }

    if (lastValue) {
      const newMax = currencyToNumber(newValue.maximumValue) ?? 0;
      const lastMax = currencyToNumber(lastValue.maximumValue) ?? 0;

      if (newMax >= lastMax) {
        toast.error(getTranslateByKey('information.approvalValueNotValid'));
        return false;
      }
    }

    return true;
  }

  function validateLevelEditing(
    currentValues: IApprovalMatrixValueFormOutput[],
    newValue: IApprovalMatrixValueFormOutput,
    editingIdx: number
  ) {
    const newMax = currencyToNumber(newValue.maximumValue) ?? 0;

    const prevValue = editingIdx > 0 ? currentValues[editingIdx - 1] : null;
    const nextValue =
      editingIdx < currentValues.length - 1
        ? currentValues[editingIdx + 1]
        : null;

    if (prevValue) {
      const prevMax = currencyToNumber(prevValue.maximumValue) ?? 0;
      if (newMax >= prevMax) {
        toast.error(getTranslateByKey('information.approvalValueNotValid'));
        return false;
      }
    }

    if (nextValue) {
      const nextMax = currencyToNumber(nextValue.maximumValue) ?? 0;
      if (newMax <= nextMax) {
        toast.error(getTranslateByKey('information.approvalValueNotValid'));
        return false;
      }
    }

    return true;
  }

  const isEditing = !isNullUndefined(editingIndex);

  function onSubmit(data: IApprovalMatrixValueFormOutput) {
    if (isEditing) {
      if (!validateLevelEditing(fields, data, editingIndex)) return;
      update(editingIndex, data);
    } else {
      if (!validateLevelAddition(fields, data)) return;
      append(data);
    }
    clearForm();
  }

  const rowHeaders = [
    <TextEllipsis key={0}>
      {getTranslateByKey('information.hierarchyLevel')}
    </TextEllipsis>,
    <TextEllipsis key={1}>
      {getTranslateByKey('information.maximumValue')}
    </TextEllipsis>,
    <RowHeaderTextEnd key={2}>
      <TextEllipsis>{getTranslateByKey('information.actions')}</TextEllipsis>
    </RowHeaderTextEnd>
  ];

  const filteredHierarchyLevelOptions = isEditing
    ? userHierarchyLevels?.filter(
        item => item.key === fields[editingIndex]?.hierarchyLevel?.key
      )
    : userHierarchyLevels?.filter(
        item =>
          !fields
            .filter(fieldItem => fieldItem.active)
            .flatMap(fieldItem => fieldItem.hierarchyLevel?.key)
            .includes(item.key)
      );

  return (
    <Box title={getTranslateByKey('information.approvalValues')}>
      <S.RowInputs>
        <KogAutoCompleteControlled
          id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-select-hierarchyLevel`}
          label={getTranslateByKey('information.hierarchyLevel')}
          control={controlValueForm}
          options={filteredHierarchyLevelOptions ?? []}
          isLoading={isLoadingUserHierarchy}
          nameCustom="hierarchyLevel"
          setValueCustom={setValue}
          isDefaultValueUnique
          onChangeCustom={() => {}}
          required
          rulesCustom={{ required: true }}
          disabled={isEditing}
        />

        <KogInputAmountControlled
          id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-input-maximumValue`}
          label={getTranslateByKey('information.maximumValue')}
          control={controlValueForm}
          name="maximumValue"
          typeInput="filled"
          required
        />

        <S.AreaButtons>
          <Button
            id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-btn-addValue`}
            onClick={handleSubmit(onSubmit)}>
            {getTranslateByKey('information.addValue')}
          </Button>

          {!isNullUndefined(editingIndex) && (
            <SecondaryButton
              id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-btn-cancel`}
              onClick={() => clearForm()}>
              {getTranslateByKey('information.cancel')}
            </SecondaryButton>
          )}
        </S.AreaButtons>
      </S.RowInputs>

      {fields.length > 0 && (
        <BoxGrid rowHeaders={rowHeaders} sizeColumns={['35%', '35%', '20%']}>
          {fields.map((item, index) => {
            if (!item.active) return null;

            return (
              <KOGTableRow $isSizeLong $notClickable $notHover key={item.id}>
                <TableCell>
                  <TextEllipsis title={item.hierarchyLevel?.value}>
                    {item.hierarchyLevel?.value}
                  </TextEllipsis>
                </TableCell>

                <TableCell>
                  <TextEllipsis title={item.maximumValue}>
                    {item.maximumValue}
                  </TextEllipsis>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-edit-${item.hierarchyLevel?.value}`}
                    title={getTranslateByKey('information.edit')}
                    onClick={() => handleEdit(item, index)}>
                    <Edit />
                  </IconButton>

                  <IconButton
                    id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-remove-${item.hierarchyLevel?.value}`}
                    title={getTranslateByKey('information.remove')}
                    onClick={() => handleRemove(item, index)}>
                    <Close />
                  </IconButton>
                </TableCell>
              </KOGTableRow>
            );
          })}
        </BoxGrid>
      )}

      <ModalConfirmation />
    </Box>
  );
}
